import React from "react";
import { Table, Input, Tag, Tooltip, Typography } from "antd";
import { faList, faNetworkWired, faUsers } from "@fortawesome/free-solid-svg-icons";
import { fmtUnix, defaultRange } from "./log-utils";
import { useLogTable } from "./hooks/use-log-table";
import LogStatCards from "./components/log-stat-cards";
import LogToolbar from "./components/log-toolbar";
import LogFilterSelect from "./components/log-filter-select";
import LogIpFilter from "./components/log-ip-filter";
import LogTimelineChart from "./components/log-timeline-chart";
import LogDetailDrawer from "./components/log-detail-drawer";
import logApi from "../../../api/log-api";

const { Text } = Typography;

// Tab xem AccessLog — ai truy cập endpoint nhạy cảm (chống scraping/abuse).
// Stat card phản ánh đúng bộ lọc hiện tại (kể cả khoảng ngày).
// options = { resource, action } lấy từ DB, do trang cha truyền xuống.
function AccessLogTab({ onRefreshStats, options }) {
    const t = useLogTable({
        name: "AccessLog",
        initialFilter: { resource: "", action: "", user_id: "", ref_id: "", ip: "", ...defaultRange() },
        load: (p) => logApi.getAccess(p),
        remove: (ids) => logApi.deleteAccess(ids),
        removeAll: (f) => logApi.deleteAllAccess(f),
        loadStats: (f) => logApi.getAccessStats(f),
        exportAll: (f) => logApi.exportCsv("access", f),
        onMutated: onRefreshStats,
    });

    const s = t.stats;
    const cards = [
        { key: "total", title: "Tổng truy cập", value: s?.total, icon: faList, iconColor: "text-blue-600", bgColor: "bg-blue-50" },
        { key: "users", title: "User khác nhau", value: s?.users, icon: faUsers, iconColor: "text-green-600", bgColor: "bg-green-50" },
        { key: "ips", title: "IP khác nhau", value: s?.ips, icon: faNetworkWired, iconColor: "text-purple-600", bgColor: "bg-purple-50" },
    ];

    const columns = [
        { title: "Thời gian", dataIndex: "created_at", width: 150, render: fmtUnix },
        { title: "Resource", dataIndex: "resource", width: 120, render: (r) => <Tag>{r}</Tag> },
        { title: "Action", dataIndex: "action", width: 130 },
        { title: "User", dataIndex: "user_id", width: 90 },
        { title: "Ref", dataIndex: "ref_id", width: 90 },
        {
            title: "IP", dataIndex: "ip_address", width: 150,
            render: (v) => v ? <Text copyable={{ text: v }} style={{ fontSize: 12 }}>{v}</Text> : "-",
        },
        {
            title: "User-Agent", dataIndex: "user_agent", ellipsis: true,
            render: (ua) => ua ? <Tooltip title={ua}>{ua}</Tooltip> : "-",
        },
    ];

    return (
        <div>
            <LogStatCards cards={cards} loading={t.statsLoading} />
            <LogToolbar t={t} onRefresh={() => { t.reload(); onRefreshStats?.(); }}>
                <LogFilterSelect
                    width={140} placeholder="Resource" values={options?.resource}
                    value={t.f.resource} onChange={(v) => t.setField("resource", v)}
                />
                <LogFilterSelect
                    width={180} placeholder="Action" values={options?.action}
                    value={t.f.action} onChange={(v) => t.setField("action", v)}
                />
                <Input style={{ width: 100 }} placeholder="User ID" allowClear
                    inputMode="numeric" value={t.f.user_id}
                    onChange={(e) => t.setField("user_id", e.target.value.replace(/\D/g, ""))} />
                <Input.Search style={{ width: 110 }} placeholder="Ref ID" allowClear
                    inputMode="numeric" value={t.f.ref_id}
                    onChange={(e) => t.setField("ref_id", e.target.value.replace(/\D/g, ""))}
                    onSearch={t.search} />
                <LogIpFilter
                    value={t.f.ip}
                    onChange={(v) => t.setField("ip", v)}
                    onSearch={t.search}
                />
            </LogToolbar>

            <LogTimelineChart type="access" filter={t.appliedF} reloadKey={t.version} color="#722ed1" />

            <Table
                rowKey="id"
                size="small"
                loading={t.loading}
                columns={columns}
                dataSource={t.rows}
                scroll={{ x: 900 }}
                rowSelection={{ selectedRowKeys: t.selected, onChange: t.setSelected }}
                onRow={(r) => ({ onClick: () => t.openDetail(r), style: { cursor: "pointer" } })}
                pagination={{
                    current: t.page, pageSize: t.limit, total: t.total,
                    showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200],
                    showTotal: (n) => `${n} dòng`,
                    onChange: t.onPageChange, onShowSizeChange: t.onPageChange,
                }}
            />

            <LogDetailDrawer row={t.detail} title="Chi tiết truy cập" onClose={t.closeDetail} />
        </div>
    );
}

export default AccessLogTab;
